import { patchSearchParams } from './scans-list.tsx'

/**
 * Where the threat model's nested views live in the URL, and how Overview's metrics address the
 * inventory sections that count them.
 *
 * All three levels are query params rather than component state so a metric can be an ordinary link:
 * middle-click opens it in a tab, the back button returns you to Overview, and a deep link into
 * "Attack surface → Lists → Sensitive assets" survives a reload. This module holds the names and the
 * mapping so `threat-model-tab.tsx` (which owns the tabs) and `threat-model-report.tsx` (which owns
 * both the metrics and the sections) can agree without importing each other.
 */

export const THREAT_MODEL_VIEW_PARAM = 'threatModelView'
export const SURFACE_VIEW_PARAM = 'surfaceView'
export const SURFACE_SECTION_PARAM = 'surfaceSection'

/** The inventory sections in the Lists view, keyed by their URL value. */
export const SURFACE_SECTIONS = {
  actors: 'Actors',
  'entry-points': 'Entry points',
  'trust-boundaries': 'Trust boundaries',
  'sensitive-assets': 'Sensitive assets',
  'security-controls': 'Security controls',
  'dependency-notes': 'Dependency notes',
} as const

export type SurfaceSectionId = keyof typeof SURFACE_SECTIONS

const SURFACE_SECTION_IDS = Object.keys(SURFACE_SECTIONS) as SurfaceSectionId[]

export const parseSurfaceSection = (raw: string | null): SurfaceSectionId | undefined =>
  SURFACE_SECTION_IDS.find(id => id === raw)

/**
 * The search string that opens one inventory section, preserving everything else already in the URL
 * (the repository tab, the selected tool, and so on).
 */
export const surfaceSectionSearch = (current: URLSearchParams, section: SurfaceSectionId): string =>
  patchSearchParams(current, {
    [THREAT_MODEL_VIEW_PARAM]: 'surface',
    [SURFACE_VIEW_PARAM]: 'list',
    [SURFACE_SECTION_PARAM]: section,
  }).toString()

/**
 * Vulnerability classes are counted in Overview but never inventoried in Lists — they are adjudicated,
 * one card per class, on their own tab. So that metric addresses a view rather than a section.
 */
export const adjudicationSearch = (current: URLSearchParams): string =>
  patchSearchParams(current, {
    [THREAT_MODEL_VIEW_PARAM]: 'adjudication',
    [SURFACE_VIEW_PARAM]: null,
    [SURFACE_SECTION_PARAM]: null,
  }).toString()
